import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getRouteLabel } from "../Layout/Sidebar";

const DEFAULT_TITLE = "Export Stuff – Ceramics Export & Trade Workflow Platform";
const DEFAULT_DESCRIPTION =
  "Enterprise SaaS platform automating international ceramic tile export documentation, quotation pipelines, container packing lists, BL drafts, and trade inventory.";
const BASE_URL = "https://exportstuff.com";
const DEFAULT_IMAGE = `${BASE_URL}/CompanyLogo.png`;

/**
 * Updates document title and meta tags (og:*, twitter:*, description) based on current route or explicit props.
 */
const PageMeta = ({ title: propTitle, description: propDescription, image: propImage }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const routeLabel = getRouteLabel(pathname);
    let resolvedTitle = propTitle;
    let resolvedDescription = propDescription;

    if (!resolvedTitle) {
      if (pathname === "/" || !routeLabel || routeLabel === "Home") {
        resolvedTitle = DEFAULT_TITLE;
      } else {
        resolvedTitle = `${routeLabel} | Export Stuff`;
      }
    }

    if (!resolvedDescription) {
      if (pathname === "/" || !routeLabel || routeLabel === "Home") {
        resolvedDescription = DEFAULT_DESCRIPTION;
      } else {
        resolvedDescription = `${routeLabel} – Export Stuff ceramics export workflow and documentation management.`;
      }
    }

    const resolvedImage = propImage || DEFAULT_IMAGE;
    const resolvedUrl = `${BASE_URL}${pathname}`;

    document.title = resolvedTitle;

    const setMeta = (attributeName, attributeValue, content) => {
      let el = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attributeName, attributeValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Standard Meta
    setMeta("name", "description", resolvedDescription);

    // Robots Meta (index public pages, noindex private dashboards)
    const PUBLIC_PATHS = ["/", "/login", "/signup", "/privacy", "/terms"];
    const isPublic = PUBLIC_PATHS.includes(pathname) || pathname.startsWith("/p/");
    setMeta(
      "name",
      "robots",
      isPublic
        ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        : "noindex, nofollow"
    );

    // OpenGraph
    setMeta("property", "og:title", resolvedTitle);
    setMeta("property", "og:description", resolvedDescription);
    setMeta("property", "og:url", resolvedUrl);
    setMeta("property", "og:image", resolvedImage);
    setMeta("property", "og:type", "website");

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", resolvedTitle);
    setMeta("name", "twitter:description", resolvedDescription);
    setMeta("name", "twitter:image", resolvedImage);
    setMeta("name", "twitter:url", resolvedUrl);
  }, [pathname, propTitle, propDescription, propImage]);

  return null;
};

export default PageMeta;
